// src/context/AuthContext.tsx
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";
import pb from "../api/pocketbase";
import { isAuthenticated, getToken, getUserId, getUser } from "../services/authService";
import { User } from "../types/auth";

export interface AuthState {
    isAuthenticated: boolean;
    token: string | null;
    userId: string | null;
    user: User | null;
    isAdmin: boolean;
}

interface AuthContextType {
    authState: AuthState;
    setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
}

const buildState = (): AuthState => {
    const user = getUser();
    return {
        isAuthenticated: isAuthenticated(),
        token: getToken(),
        userId: getUserId(),
        user,
        isAdmin: user?.role === "admin",
    };
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [authState, setAuthState] = useState<AuthState>(buildState);

    useEffect(() => {
        // Keep context in sync with PocketBase's auth store (login, logout,
        // token refresh, profile updates all flow through here).
        const unsubscribe = pb.authStore.onChange(() => {
            setAuthState(buildState());
        });
        // Re-sync once on mount in case the store changed before subscribing.
        setAuthState(buildState());
        return () => unsubscribe();
    }, []);

    return (
        <AuthContext.Provider value={{ authState, setAuthState }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
